"use client"
/* eslint-disable react-hooks/exhaustive-deps */

import { useState, useEffect, useContext, useCallback } from "react"
import axios from "axios"
import { AuthContext } from "../../../Auth/context/AuthContext"
import { ArrowLeft, Loader2, AlertCircle } from "lucide-react"
import type { ImportRow } from "../../types"

interface PreviewStepProps {
  excelFile: File
  finalSchema: string[]
  sampleHeaders: string[]
  sampleRows: string[][]
  exampleRows: Record<string, string>[]
  onSuccess: (records: ImportRow[], jobId: string | null) => void
  onBack: () => void
  onError: (error: string) => void
}

import useContactTypes from '../../hooks/useContactTypes';
// helper: true when ID came from DB (24-char hex), false when provisional TMP_*
const isDbId = (id?: string) => /^[a-f0-9]{24}$/.test(id || '');
export default function PreviewStep({
  excelFile,
  finalSchema,
  sampleHeaders,
  sampleRows,
  exampleRows,
  onSuccess,
  onBack,
  onError,
}: PreviewStepProps) {
  const { auth } = useContext(AuthContext)
  const [loading, setLoading] = useState(true)
  const [parsedRecords, setParsedRecords] = useState<ImportRow[]>([])
  const [selectedRows, setSelectedRows] = useState<Record<string, "insert" | "skip" | "overwrite">>({})
  const [saving, setSaving] = useState(false)
  const contactTypes = useContactTypes();

  // Run preview on any input change (we intentionally omit previewImport from deps)
  useEffect(() => { previewImport() }, [excelFile, finalSchema, sampleHeaders, sampleRows, exampleRows, auth?.token]) // eslint-disable-line react-hooks/exhaustive-deps

  const previewImport = async () => {
    if (!excelFile || !auth?.token) return

    setLoading(true)
    onError("")

    try {
      const form = new FormData()
      form.append("excelFile", excelFile)
      form.append("finalSchema", JSON.stringify(finalSchema))
      form.append("sampleHeaders", JSON.stringify(sampleHeaders))
      form.append("sampleRows", JSON.stringify(sampleRows))
      form.append("exampleRows", JSON.stringify(exampleRows))
      form.append("preview", "true")
      const resp = await axios.post(
        "/api/myteconstruction/contact-import/import",
        form,
        { headers: { Authorization: `Bearer ${auth.token}` } }
      )
      // Enriched parsed records from server include mappedRow and conflict info
      const fetched: ImportRow[] = resp.data.parsedRecords || [];
      setParsedRecords(fetched);
      // Pre-select all rows as 'insert' so summary counters are accurate
      const initialSelection: Record<number, "insert"> = Object.fromEntries(
        fetched.map((_, i) => [i, "insert"])
      );
      setSelectedRows(initialSelection);
    } catch (err) {
      console.error("Preview import error:", err)
      onError("Failed to preview import. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  // Edit in-place: update mappedRow when user changes a field
  const handleFieldEdit = useCallback(
    (rowIndex: number, fieldKey: string, newValue: string) => {
      setParsedRecords((rows) => {
        const updated = [...rows];
        const target = updated[rowIndex];
        updated[rowIndex] = {
          ...target,
          mappedRow: {
            ...target.mappedRow,
            [fieldKey]: newValue,
          },
        };
        return updated;
      });
    }, [setParsedRecords]
  );

  const handleRowActionChange = (index: number, action: "insert" | "skip" | "overwrite") => {
    setSelectedRows((prev) => ({
      ...prev,
      [index]: action,
    }))
  }

  const handleConfirmImport = async () => {
    if (!excelFile || !auth?.token) return

    setSaving(true)
    onError("")

    try {
      const newRows = parsedRecords.filter((_, i) => selectedRows[i] === "insert")
      const upsertRows = parsedRecords
        .map((r, i) => ({ newData: r, action: selectedRows[i] }))
        .filter((x) => x.action === "overwrite")
        .map((x) => ({ newData: x.newData }))

      const payload = { newRows, upsertRows }
      const resp = await axios.post(
        "/api/myteconstruction/contact-import/confirm",
        payload,
        { headers: { Authorization: `Bearer ${auth.token}` } }
      )
      // Handle synchronous vs queued import
      const { jobId, insertedCount, upsertCount } = resp.data;
      if (jobId) {
        // queued import: proceed to progress step
        onSuccess(parsedRecords, jobId);
      } else if (insertedCount || upsertCount) {
        // synchronous import completed: finish immediately
        onSuccess(parsedRecords, null);
      } else {
        throw new Error('No contacts were imported.');
      }
    } catch (err) {
      console.error("Confirm import error:", err)
      onError("Failed to import contacts. Please try again.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <h3 className="text-lg font-medium text-white mb-4">Review & Confirm</h3>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : (
        <div className="space-y-6">
          {parsedRecords.length > 0 && (
            <div className="flex justify-end gap-2 mb-4">
              <button
                type="button"
                className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-md text-xs"
                onClick={() => {
                  const allSkip = Object.fromEntries(
                    parsedRecords.map((_, i) => [
                      i.toString(),
                      "skip"
                    ])
                  ) as Record<string, "insert" | "skip" | "overwrite">;
                  setSelectedRows(allSkip);
                }}
              >
                Ignore All Conflicts
              </button>
              <button
                type="button"
                className="px-3 py-1.5 bg-blue-700 hover:bg-blue-600 rounded-md text-xs text-white"
                onClick={() => {
                  const mass = Object.fromEntries(
                    parsedRecords.map((r, i) => [
                      i.toString(),
                      r.conflictType ? "overwrite" : "insert"
                    ])
                  ) as Record<string, "insert" | "skip" | "overwrite">;
                  setSelectedRows(mass);
                }}
              >
                Overwrite All Conflicts
              </button>
            </div>
          )}
          <div className="bg-gray-800 p-4 rounded-lg">
            <h4 className="font-medium text-gray-300 mb-3">Import Summary</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-gray-700 p-3 rounded-lg">
                <p className="text-sm text-gray-400">Total Rows</p>
                <p className="text-xl font-medium text-white">{parsedRecords.length}</p>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <p className="text-sm text-gray-400">Ready to Import</p>
                <p className="text-xl font-medium text-green-400">
                  {Object.values(selectedRows).filter((action) => action === "insert" || action === "overwrite").length}
                </p>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <p className="text-sm text-gray-400">Skipped</p>
                <p className="text-xl font-medium text-yellow-400">
                  {Object.values(selectedRows).filter((action) => action === "skip").length}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-medium text-gray-300">Review Contacts</h4>

            {parsedRecords.length > 0 ? (
              <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                {parsedRecords.map((record, index) => (
                  <div
                    key={index}
                    className={`bg-gray-800 p-4 rounded-lg border ${
                      record.conflictType ? "border-yellow-700" : "border-gray-700"
                    }`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h5 className="font-medium text-white">{record.mappedRow?.Name ?? `Contact #${index + 1}`}</h5>
                        <p className="text-sm text-gray-400">
                          {record.mappedRow?.Email ?? "No email"} • {record.mappedRow?.["Contact Type"] ?? "No type"}
                        </p>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleRowActionChange(index, "skip")}
                          className={`px-3 py-1 rounded-md text-sm ${
                            selectedRows[index] === "skip"
                              ? "bg-yellow-600 text-white"
                              : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                          }`}
                        >
                          Skip
                        </button>
                        <button
                          onClick={() => handleRowActionChange(index, "insert")}
                          className={`px-3 py-1 rounded-md text-sm ${
                            selectedRows[index] === "insert"
                              ? "bg-green-600 text-white"
                              : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                          }`}
                          disabled={record.conflictType === "email"}
                        >
                          Insert
                        </button>
                        {record.conflictType && (
                          <button
                            onClick={() => handleRowActionChange(index, "overwrite")}
                            className={`px-3 py-1 rounded-md text-sm ${
                              selectedRows[index] === "overwrite"
                                ? "bg-blue-600 text-white"
                                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                            }`}
                          >
                            Overwrite
                          </button>
                        )}
                      </div>
                    </div>

                    {record.conflictType && (
                      <div className="bg-yellow-900/30 border border-yellow-700 p-3 rounded-lg mb-3 flex items-start gap-2">
                        <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5" />
                        <div>
                          <p className="text-sm text-yellow-300">
                            {record.conflictType === "email"
                              ? "Email already exists in your contacts."
                              : "Duplicate email in this import batch."}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            Choose to skip this contact, or overwrite the existing one.
                          </p>
                        </div>
                      </div>
                    )}
                    {/* Inline editable fields for quick adjustments */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                      {Object.entries(record.mappedRow).map(([key, value]) => {
                        // Contact Kind: Individual vs Business
                        if (key === "Contact Type") {
                          return (
                            <div key={key} className="flex flex-col">
                              <label className="text-xs text-gray-400 mb-1">{key}</label>
                              <select
                                name={key}
                                value={value}
                                onChange={(e) => handleFieldEdit(index, key, e.target.value)}
                                className="bg-gray-700 text-white p-2 rounded border border-gray-600 text-sm"
                              >
                                <option value="Individual">Individual</option>
                                <option value="Business">Business</option>
                              </select>
                            </div>
                          )
                        }
                        // Sub-Type: pick from predefined roles
                        if (key === "Sub-Type") {
                          return (
                            <div key={key} className="flex flex-col">
                              <label className="text-xs text-gray-400 mb-1">{key}</label>
                              <select
                                name={key}
                                value={value}
                                onChange={(e) => handleFieldEdit(index, key, e.target.value)}
                                className="bg-gray-700 text-white p-2 rounded border border-gray-600 text-sm"
                              >
                                <option value="">Select sub-type</option>
                                {contactTypes.map((role: string) => (
                                  <option key={role} value={role}>{role}</option>
                                ))}
                              </select>
                            </div>
                          )
                        }
                        // Default free-text fields
                        return (
                          <div key={key} className="flex flex-col">
                            <label className="text-xs text-gray-400 mb-1">{key}</label>
                            <input
                              type="text"
                              name={key}
                              value={value}
                              onChange={(e) => handleFieldEdit(index, key, e.target.value)}
                              className="bg-gray-700 text-white p-2 rounded border border-gray-600 text-sm"
                            />
                          </div>
                        )
                      })}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <h6 className="text-xs text-gray-400 mb-1">Contact Details</h6>
                        <div className="bg-gray-700 p-2 rounded-lg space-y-1 text-sm">
                          <p>
                            <span className="text-gray-400">Name:</span> {record.mappedRow.Name || "N/A"}
                          </p>
                          <p>
                            <span className="text-gray-400">Email:</span> {record.mappedRow.Email || "N/A"}
                          </p>
                          <p>
                            <span className="text-gray-400">Phone:</span> {record.mappedRow.Number || "N/A"}
                          </p>
                          <p>
                            <span className="text-gray-400">Type:</span> {record.mappedRow["Contact Type"] || "N/A"}
                          </p>
                          <p>
                            <span className="text-gray-400">Role:</span> {record.mappedRow["Sub-Type"] || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div>
                        <h6 className="text-xs text-gray-400 mb-1">Company & Office</h6>
                        <div className="bg-gray-700 p-2 rounded-lg space-y-1 text-sm">
                          <p>
                            <span className="text-gray-400">Company:</span> {record.mappedRow["Company Name"] || "N/A"}
                            {record.finalCompanyId && (
                              <span
                                className={`ml-2 px-1.5 py-0.5 text-xs rounded-full ${
                                  isDbId(record.finalCompanyId)
                                    ? 'bg-orange-600 text-white'
                                    : 'bg-green-900/50 text-green-300'
                                }`}
                              >
                                {isDbId(record.finalCompanyId) ? 'Existing' : 'New'}
                              </span>
                            )}
                          </p>
                          <p>
                            <span className="text-gray-400">Office:</span> {record.mappedRow["Office Name"] || "N/A"}
                            {record.finalOfficeId && (
                              <span
                                className={`ml-2 px-1.5 py-0.5 text-xs rounded-full ${
                                  isDbId(record.finalOfficeId)
                                    ? 'bg-orange-600 text-white'
                                    : 'bg-green-900/50 text-green-300'
                                }`}
                              >
                                {isDbId(record.finalOfficeId) ? 'Existing' : 'New'}
                              </span>
                            )}
                          </p>
                          <p>
                            <span className="text-gray-400">Address:</span> {record.mappedRow.Address || "N/A"}
                          </p>
                          <p>
                            <span className="text-gray-400">City:</span> {record.mappedRow.City || "N/A"}
                          </p>
                          <p>
                            <span className="text-gray-400">Location:</span> {record.mappedRow.StateProvince || "N/A"},{" "}
                            {record.mappedRow.Country || "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-800 p-6 rounded-lg text-center">
                <AlertCircle className="h-8 w-8 mx-auto mb-2 text-yellow-500" />
                <p className="text-gray-300">No records found in the file.</p>
              </div>
            )}
          </div>

          <div className="flex justify-between pt-2">
            <button
              onClick={onBack}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </button>
            <button
              onClick={handleConfirmImport}
              disabled={
                saving ||
                Object.values(selectedRows).filter((action) => action === "insert" || action === "overwrite").length ===
                  0
              }
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Importing...</span>
                </>
              ) : (
                <span>Confirm Import</span>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
